import { createSignal } from 'solid-js';

/** Maximum number of runtime events kept in the in-memory ring buffer.
 *  500 covers many hours of normal session activity; events fire rarely
 *  compared to geolocation fixes, so a smaller cap than the fix buffer
 *  is adequate. */
export const EVENT_BUFFER_CAP = 500;

/** Discrete platform/runtime events we care about during a field test.
 *  See docs/research/02-technical-architecture.md Addendum A for why
 *  these specific signals are worth capturing alongside fixes. */
export type EventType =
  | 'visibility_change'
  | 'wake_lock_acquired'
  | 'wake_lock_released'
  | 'wake_lock_reacquired'
  | 'geolocation_permission_changed'
  | 'geolocation_error'
  | 'tracking_started'
  | 'tracking_stopped';

export interface RuntimeEvent {
  /** Epoch milliseconds at the moment the event was pushed. */
  timestamp: number;
  type: EventType;
  payload: Record<string, unknown>;
}

const [events, setEvents] = createSignal<RuntimeEvent[]>([]);

/** Module-level accessor for the event buffer. Components subscribe by
 *  calling this; lib modules push via {@link pushEvent}. Using a module
 *  singleton (rather than a global bus / context) because every source
 *  shares the same buffer and the coupling is one-directional. */
export function eventsAccessor() {
  return events();
}

/** Append an event to the ring buffer. Trims to {@link EVENT_BUFFER_CAP}. */
export function pushEvent(type: EventType, payload: Record<string, unknown> = {}): void {
  const ev: RuntimeEvent = { timestamp: Date.now(), type, payload };
  setEvents((list) => {
    const next = [...list, ev];
    if (next.length > EVENT_BUFFER_CAP) next.splice(0, next.length - EVENT_BUFFER_CAP);
    return next;
  });
}

/** Wipe the buffer. Used by the DIAG clear button. */
export function clearEvents(): void {
  setEvents([]);
}
